import React from 'react';
import { Button, Typography } from 'antd';
import {
  BarChartOutlined,
  CheckSquareOutlined,
  CloseOutlined,
  FileTextOutlined,
  FormOutlined,
  MessageOutlined,
  RightOutlined,
  SearchOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';

const { Text } = Typography;

const ICON_BY_KEY = {
  analysis: BarChartOutlined,
  artifact_output: FileTextOutlined,
  checklist: CheckSquareOutlined,
  chat_followup: MessageOutlined,
  requirement_intake: FormOutlined,
  sourcing: SearchOutlined,
};

function normalizeText(value) {
  return String(value || '').trim();
}

function firstTruthy(...values) {
  return values.find(Boolean);
}

function conditionalValue(condition, whenTrue, whenFalse = null) {
  return condition ? whenTrue : whenFalse;
}

function resolvePromptCardIcon(iconKey, family) {
  return firstTruthy(ICON_BY_KEY[normalizeText(iconKey)], ICON_BY_KEY[normalizeText(family)], MessageOutlined);
}

/**
 * Render backend-recommended next actions as a compact agent action strip.
 */
export default function RecommendationActionStrip(props) {
  const {
  themeTokens,
  title,
  compact,
  canExpandScenarios,
  showAllScenarios,
  onToggleShowAllScenarios,
  visibleScenarios,
  onScenarioCardClick,
  onDismiss,
  resolvedUILabels,
  } = {
    title: '', compact: false, canExpandScenarios: false, showAllScenarios: false,
    visibleScenarios: [], resolvedUILabels: {}, ...props,
  };
  return (
    <div
      className="flare-recommendation-action-strip"
      style={{
        alignItems: 'center',
        background: 'transparent',
        border: 0,
        boxShadow: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: conditionalValue(compact, 4, 6),
        minWidth: 0,
        padding: conditionalValue(compact, '0 1px 1px', '0 2px 2px'),
      }}
    >
      <div
        className="flare-recommendation-action-head"
        style={{
          alignItems: 'center',
          display: 'flex',
          flex: '0 0 auto',
          gap: 8,
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <div
          className="flare-recommendation-action-title"
          style={{
            alignItems: 'center',
            display: 'flex',
            gap: 6,
            minWidth: 0,
          }}
        >
          <ThunderboltOutlined style={{ color: themeTokens.bubbleUserBg }} />
          <Text strong style={{ color: themeTokens.textPrimary, fontSize: 12, fontWeight: 500 }}>
            {title}
          </Text>
        </div>
        <div
          className="flare-recommendation-action-controls"
          style={{
            alignItems: 'center',
            display: 'flex',
            flex: '0 0 auto',
            gap: 4,
          }}
        >
          {conditionalValue(canExpandScenarios, (
            <Button
              onClick={onToggleShowAllScenarios}
              size="small"
              style={{ color: themeTokens.textSecondary, flex: '0 0 auto', paddingInline: 0 }}
              type="text"
            >
              {conditionalValue(showAllScenarios,
                firstTruthy(resolvedUILabels.scenario_collapse_label, '收起'),
                firstTruthy(resolvedUILabels.scenario_expand_label, '更多'))}
            </Button>
          ))}
          <Button
            aria-label={firstTruthy(resolvedUILabels.scenario_dismiss_label, '隐藏建议')}
            icon={<CloseOutlined />}
            onClick={onDismiss}
            size="small"
            style={{ color: themeTokens.textSecondary, height: 22, minWidth: 22, paddingInline: 0 }}
            type="text"
          />
        </div>
      </div>

      <div
        className="flare-recommendation-action-list"
        style={{
          display: 'flex',
          flexDirection: 'column',
          flexWrap: 'nowrap',
          alignItems: 'stretch',
          flex: '1 1 auto',
          gap: 0,
          minWidth: 0,
          overflow: 'hidden',
          padding: '1px 0',
          width: '100%',
        }}
      >
        {visibleScenarios.map((entry, index) => {
          const promptPreview = normalizeText(entry.promptPreview);
          const secondaryText = firstTruthy(promptPreview, normalizeText(entry.description));
          const PromptIcon = resolvePromptCardIcon(entry.iconKey, entry.family);
          return (
            <button
              className="flare-recommendation-action-pill"
              key={firstTruthy(entry.key, entry.label) ?? index}
              onClick={() => {
                if (typeof onScenarioCardClick === 'function') onScenarioCardClick(entry);
              }}
              style={{
                alignItems: 'center',
                appearance: 'none',
                background: 'transparent',
                border: 0,
                borderBottom: conditionalValue(index < visibleScenarios.length - 1, `1px solid ${themeTokens.divider}`, 0),
                borderRadius: 0,
                color: themeTokens.textPrimary,
                cursor: 'pointer',
                display: 'grid',
                flex: '0 0 auto',
                font: 'inherit',
                gap: conditionalValue(compact, 8, 10),
                gridTemplateColumns: 'minmax(0, 1fr) auto',
                maxWidth: '100%',
                minHeight: 46,
                minWidth: 0,
                padding: '14px 0',
                textAlign: 'left',
              }}
              title={firstTruthy(promptPreview, entry.description, entry.label)}
              type="button"
            >
              <span
                className="flare-recommendation-action-pill-body"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: conditionalValue(compact, 2, 4),
                  minWidth: 0,
                }}
              >
                <span
                  className="flare-recommendation-action-pill-label"
                  style={{
                    alignItems: 'center',
                    display: 'inline-flex',
                    flex: '0 0 auto',
                    fontSize: 14,
                    fontWeight: 500,
                    gap: 6,
                    lineHeight: 1.6,
                    width: '100%',
                  }}
                >
                  <span
                    aria-hidden="true"
                    className="flare-recommendation-action-pill-icon"
                    style={{ color: themeTokens.bubbleUserBg, display: 'inline-flex', flex: '0 0 auto' }}
                  >
                    <PromptIcon />
                  </span>
                  <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {entry.label}
                  </span>
                </span>
                {conditionalValue(Boolean(secondaryText), (
                  <span
                    className="flare-recommendation-action-pill-description"
                    style={{
                      color: themeTokens.textSecondary,
                      fontSize: 12,
                      flex: '0 1 auto',
                      lineHeight: 1.6,
                      minWidth: 0,
                      display: '-webkit-box',
                      overflow: 'hidden',
                      overflowWrap: 'anywhere',
                      textOverflow: 'ellipsis',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: compact ? 1 : 2,
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      width: '100%',
                    }}
                  >
                    {secondaryText}
                  </span>
                ))}
              </span>
              <span
                className="flare-recommendation-action-pill-arrow"
                style={{
                  alignItems: 'center',
                  alignSelf: 'center',
                  color: themeTokens.bubbleUserBg,
                  display: 'inline-flex',
                  flex: '0 0 auto',
                  fontSize: 12,
                  justifyContent: 'center',
                  lineHeight: 1,
                  opacity: 0.72,
                }}
              >
                <RightOutlined />
              </span>
            </button>
          );
        })}
      </div>

    </div>
  );
}
